/**
 * Export Form Responses to CSV
 *
 * Converts an array of raw form response objects into a downloadable CSV file.
 * Handles special characters, commas, and quotes in field values.
 */

import { formatResponseForTable } from './formResponseFormatters';

/**
 * Escape a single CSV cell value.
 * Wraps in double-quotes if the value contains commas, quotes, or newlines.
 * @param {string} value
 * @returns {string}
 */
function escapeCsvCell(value) {
    if (value == null) return '';
    const str = String(value);
    // If contains comma, double-quote, or newline → wrap in quotes and escape inner quotes
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

/**
 * Build CSV string from rows.
 * @param {string[]} headers - Column headers
 * @param {string[][]} rows - Array of row arrays (each inner array = one row of cell values)
 * @returns {string} - Complete CSV string including BOM for Excel compatibility
 */
function buildCsvString(headers, rows) {
    const headerLine = headers.map(escapeCsvCell).join(',');
    const dataLines = rows.map(row => row.map(escapeCsvCell).join(','));
    // UTF-8 BOM ensures Excel opens the file with correct encoding
    return '\uFEFF' + [headerLine, ...dataLines].join('\r\n');
}

/**
 * Trigger browser download of a string as a file.
 * @param {string} content - File content
 * @param {string} filename - Download filename
 * @param {string} mimeType - MIME type
 */
function downloadFile(content, filename, mimeType = 'text/csv;charset=utf-8;') {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Export form responses as a CSV file.
 *
 * @param {Array} responses - Raw form response objects from Firestore
 * @param {Object} options
 * @param {Object} [options.propertiesMap] - Map of propertyId → display name
 * @param {string} [options.portfolioName] - Portfolio display name
 * @returns {{ success: boolean, count: number }}
 */
export function exportFormResponsesCsv(responses, options = {}) {
    if (!responses || responses.length === 0) {
        return { success: false, count: 0 };
    }

    const { propertiesMap = {}, portfolioName = '' } = options;
    const formatContext = { propertiesMap, portfolioName };

    // CSV columns (match the table exactly)
    const headers = ['Contact Name', 'Email', 'Source Page', 'Received'];

    // Convert each response to a formatted row
    const rows = responses.map(response => {
        const formatted = formatResponseForTable(response, formatContext);
        return [
            formatted.name || '',
            formatted.email || '',
            formatted.source || '',
            formatted.submittedAtFormatted || '',
        ];
    });

    // Build CSV
    const csv = buildCsvString(headers, rows);

    // Generate filename: FormResponses_YYYY-MM-DD.csv
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const filename = `FormResponses_${yyyy}-${mm}-${dd}.csv`;

    downloadFile(csv, filename);

    return { success: true, count: responses.length };
}
